import { ArrowRightIcon } from "@heroicons/react/24/solid";
import { ContentItemGrid } from "~/components/ContentItemGrid";
import { ContentItemWithImage } from "~/components/ContentItemWithImage";
import {
  isCTASimpleCenteredBlock,
  isCTASplitWithImage,
  isCTATwoColumnWithImage,
  isContentWithImageBlock,
  isContentWithImageQuoteCTA,
  isFAQ,
  isFeatureTwoByTwoGrid,
  isFeatureTwoTextColumns,
  isFeatureWithCTAs,
  isFeatureWithDemo,
  isHeaderSimple,
  isHeaderWithBackgroundImage,
  isHeaderWithCards,
  isHeroCentered,
  isHeroNewsletterSignup,
  isHeroWithDemo,
  isHeroWithRightImage,
  isHeroWithScreenshot,
  isInvalidPropertiesError,
  isNewsletterSignup,
  isStatsGrid,
  isStatsSimple,
  isStatsWithImage,
  isTaggedPostsBlock,
  isTestimonialSimpleCentered,
  isUnknownBlockType,
  isWysiwygBlock,
  isYoutubeEmbedBlock,
} from "~/components/LandingPage/landing-page-block-type-guards";
import { SplitWithImageContainer } from "~/components/LandingPageV2/components/SplitWithImageContainer";
import { YouTubeEmbedBlock } from "~/components/YouTubeEmbedBlock/YouTubeEmbedBlock";
import { CTASimpleCentered } from "~/landing-pages/components/CTA/CTASimpleCentered/CTASimpleCentered";
import { CTASplitWithImage } from "~/landing-pages/components/CTA/CTASplitWithImage/CTASplitWithImage";
import { CTATwoColumnWithImage } from "~/landing-pages/components/CTA/CTATwoColumnWithImage/CTATwoColumnWithImage";
import { ContentSection } from "~/landing-pages/components/ContentSections/ContentSection/ContentSection";
import { ContentSectionWithTestimonial } from "~/landing-pages/components/ContentSections/ContentSectionWithTestimonial/ContentSectionWithTestimonial";
import { InvalidProperties } from "~/landing-pages/components/Errors/InvalidProperties/InvalidProperties";
import { UnknownBlockType } from "~/landing-pages/components/Errors/UnknownBlockType/UnknownBlockType";
import { FAQAccordion } from "~/landing-pages/components/FAQ/FAQAccordion";
import { FAQSideBySide } from "~/landing-pages/components/FAQ/FAQSideBySide";
import { FeatureTwoByTwoGrid } from "~/landing-pages/components/Feature/FeatureTwoByTwoGrid";
import { FeatureTwoTextColumns } from "~/landing-pages/components/Feature/FeatureTwoTextColumns";
import { FeatureWithCTAs } from "~/landing-pages/components/Feature/FeatureWithCTAs";
import { FeatureWithDemoImplementation } from "~/landing-pages/components/Feature/FeatureWithDemoImplementation/FeatureWithDemoImplementation";
import { HeaderSimple } from "~/landing-pages/components/Header/HeaderSimple";
import { HeaderWithBackgroundImage } from "~/landing-pages/components/Header/HeaderWithBackgroundImage";
import { HeaderWithCards } from "~/landing-pages/components/Header/HeaderWithCards";
import { HeroCentered } from "~/landing-pages/components/Hero/HeroCentered/HeroCentered";
import { HeroNewsletterSignup } from "~/landing-pages/components/Hero/HeroNewsletterSignup";
import { HeroWithRightImage } from "~/landing-pages/components/Hero/HeroSplitWithRightImage/HeroWithRightImage";
import { HeroWithAppScreenshot } from "~/landing-pages/components/Hero/HeroWithAppScreenshot/HeroWithAppScreenshot";
import { HeroWithDemoImplementation } from "~/landing-pages/components/Hero/HeroWithDemoImplementation";
import { HeroWithOffsetImage } from "~/landing-pages/components/Hero/HeroWithOffsetImage/HeroWithOffsetImage";
import { NewsletterSignup } from "~/landing-pages/components/Newsletter/NewsletterSignup";
import { StatsSimple } from "~/landing-pages/components/Stats/StatsSimple";
import { StatsSimpleGrid } from "~/landing-pages/components/Stats/StatsSimpleGrid";
import { StatsSplitWithImage } from "~/landing-pages/components/Stats/StatsSplitWIthImage";
import { TestimonialSimpleCentered } from "~/landing-pages/components/Testimonial/SimpleCentered/TestimonialSimpleCentered";
import { WysiwygBlock } from "~/landing-pages/components/Wysiwyg/WysiwygBlock";
import {
  PostToRoutePostTypes,
  getRouteFromPostType,
} from "~/lib/shared/getRouteFromPostType";
import { getValueOrDefault } from "~/lib/shared/getValueOrDefault";
import { Body, Headline, Link } from "~/ui-library";
import { LandingPageBlock } from "./schemas";

export const LandingPageBlocks = ({
  blocks,
  preview = false,
}: {
  blocks: LandingPageBlock[];
  preview?: boolean;
}): JSX.Element | null => {
  if (!blocks?.length) {
    return null;
  }
  const contentBlockComponents = blocks.map((block, i) => {
    /**
     * Content
     */
    if (isContentWithImageBlock(block)) {
      return (
        <SplitWithImageContainer
          imagePosition={
            block.imagePosition === "right-sticky" ? "right" : "left"
          }
          imageAlt={block.image.alt}
          imageUrl={block.image.url}
          key={`${block.acfFcLayout}-${i}`}
          verticalTextAlignment="top"
        >
          <ContentSection {...block} contentBlocks={block.subContentBlocks} />
        </SplitWithImageContainer>
      );
    }
    if (isContentWithImageQuoteCTA(block)) {
      return (
        <ContentSectionWithTestimonial
          key={`${block.acfFcLayout}-${i}`}
          blockIndex={i}
          {...block}
          contentBlocks={block.subContentBlocks}
          quote={getValueOrDefault(block.quote, undefined)}
          imageUrl={block.image.url}
          imageAlt={block.image.alt}
          primaryCTALink={block.primaryCta.url}
          primaryCTAText={block.primaryCta.title}
        />
      );
    }
    /**
     * CTAs
     */
    if (isCTASimpleCenteredBlock(block)) {
      return (
        <CTASimpleCentered
          key={`${block.acfFcLayout}-${i}`}
          blockIndex={i}
          {...block}
          primaryCtaText={block.primaryCtaLink.title}
          primaryCtaLink={block.primaryCtaLink.url}
          secondaryCtaText={block.secondaryCtaLink?.title}
          secondaryCtaLink={block.secondaryCtaLink?.url}
        />
      );
    }
    if (isCTASplitWithImage(block)) {
      return (
        <CTASplitWithImage
          key={`${block.acfFcLayout}-${i}`}
          blockIndex={i}
          {...block}
          imageUrl={block.image.url}
          imageAlt={block.image.alt}
          primaryCtaText={block.primaryCtaLink.title}
          primaryCtaLink={block.primaryCtaLink.url}
        />
      );
    }
    if (isCTATwoColumnWithImage(block)) {
      return (
        <CTATwoColumnWithImage
          key={`${block.acfFcLayout}-${i}`}
          blockIndex={i}
          {...block}
          imageUrl={block.image.url}
          primaryCtaText={block.primaryCtaLink.title}
          primaryCtaLink={block.primaryCtaLink.url}
        />
      );
    }

    /**
     * Headers
     */
    if (isHeaderSimple(block)) {
      return (
        <HeaderSimple
          key={`${block.acfFcLayout}-${i}`}
          {...block}
          textLocation={getValueOrDefault(block.textLocation, undefined)}
          textAlignment={getValueOrDefault(block.textAlignment, undefined)}
          titleLevel={getValueOrDefault(block.titleLevel, "h2")}
        />
      );
    }
    if (isHeaderWithBackgroundImage(block)) {
      return (
        <HeaderWithBackgroundImage
          key={`${block.acfFcLayout}-${i}`}
          {...block}
          imageUrl={block.image.url}
          textLocation={getValueOrDefault(block.textLocation, undefined)}
          textAlignment={getValueOrDefault(block.textAlignment, undefined)}
          titleLevel={getValueOrDefault(block.titleLevel, "h2")}
        />
      );
    }
    if (isHeaderWithCards(block)) {
      return (
        <HeaderWithCards
          key={`${block.acfFcLayout}-${i}`}
          {...block}
          imageUrl={block.image?.url}
          textLocation={getValueOrDefault(block.textLocation, undefined)}
          textAlignment={getValueOrDefault(block.textAlignment, undefined)}
          titleLevel={getValueOrDefault(block.titleLevel, "h2")}
        />
      );
    }

    /**
     * Heros
     */
    if (isHeroCentered(block)) {
      return (
        <HeroCentered
          key={`${block.acfFcLayout}-${i}`}
          blockIndex={i}
          {...block}
          backgroundImage={block.backgroundImage?.url}
          kickerCtaLink={block.kickerCtaLink?.url}
          kickerCtaText={block.kickerCtaLink?.title}
          primaryCtaText={block.primaryCtaLink.title}
          primaryCtaLink={block.primaryCtaLink.url}
          secondaryCtaText={block.secondaryCtaLink?.title}
          secondaryCtaLink={block.secondaryCtaLink?.url}
        />
      );
    }
    if (isHeroNewsletterSignup(block)) {
      const { ad } = block;
      return (
        <HeroNewsletterSignup
          key={`hero-newsletter-${ad.slug}-${i}`}
          appearance={block.appearance}
          blockIndex={i}
          description={block.description}
          headline={block.headline}
          iterableListId={ad.iterableListId}
          newsletterSlug={ad.slug}
          backgroundImage={block.backgroundImage?.url}
        />
      );
    }
    if (isHeroWithDemo(block)) {
      return (
        <HeroWithDemoImplementation
          key={`${block.acfFcLayout}-${i}`}
          {...block}
        />
      );
    }
    if (isHeroWithScreenshot(block)) {
      return block.imagePlacement === "beneath" ? (
        <HeroWithAppScreenshot
          key={`${block.acfFcLayout}-${i}`}
          blockIndex={i}
          {...block}
          imageUrl={block.image.url}
          imageAlt={block.image.alt}
          primaryCtaText={block.primaryCtaLink.title}
          primaryCtaLink={block.primaryCtaLink.url}
          secondaryCtaText={block.secondaryCtaLink?.title}
          secondaryCtaLink={block.secondaryCtaLink?.url}
        />
      ) : (
        <HeroWithOffsetImage
          key={`${block.acfFcLayout}-${i}`}
          blockIndex={i}
          {...block}
          imageUrl={block.image.url}
          imageAlt={block.image.alt}
          primaryCtaText={block.primaryCtaLink.title}
          primaryCtaLink={block.primaryCtaLink.url}
          secondaryCtaText={block.secondaryCtaLink?.title}
          secondaryCtaLink={block.secondaryCtaLink?.url}
        />
      );
    }

    if (isHeroWithRightImage(block)) {
      return (
        <HeroWithRightImage
          key={`${block.acfFcLayout}-${i}`}
          blockIndex={i}
          {...block}
          imageUrl={block.image.url}
          imageAlt={block.image.alt}
          kickerCtaLink={block.kickerCtaLink?.url}
          kickerCtaText={block.kickerCtaLink?.title}
          primaryCtaText={block.primaryCtaLink.title}
          primaryCtaLink={block.primaryCtaLink.url}
          secondaryCtaText={block.secondaryCtaLink?.title}
          secondaryCtaLink={block.secondaryCtaLink?.url}
          iconImageAlt={getValueOrDefault(block.iconImage?.alt, "")}
          iconImageUrl={block.iconImage?.url}
          iconImageHeight={block.iconImage?.height}
          iconImageWidth={block.iconImage?.width}
        />
      );
    }

    /**
     * Stats
     */
    if (isStatsSimple(block)) {
      return <StatsSimple key={`${block.acfFcLayout}-${i}`} {...block} />;
    }
    if (isStatsGrid(block)) {
      return <StatsSimpleGrid key={`${block.acfFcLayout}-${i}`} {...block} />;
    }
    if (isStatsWithImage(block)) {
      return (
        <StatsSplitWithImage
          key={`${block.acfFcLayout}-${i}`}
          {...block}
          imageUrl={block.image.url}
          imageAlt={block.image.alt}
        />
      );
    }

    /**
     * FAQ
     */
    if (isFAQ(block)) {
      return block.display === "accordion" ? (
        <FAQAccordion key={`${block.acfFcLayout}-${i}`} {...block} />
      ) : (
        <FAQSideBySide key={`${block.acfFcLayout}-${i}`} {...block} />
      );
    }

    /**
     * Testimonial
     */
    if (isTestimonialSimpleCentered(block)) {
      return (
        <TestimonialSimpleCentered
          key={`${block.acfFcLayout}-${i}`}
          {...block}
          personImageUrl={block.personImage.url}
          personImageAlt={block.personImage.alt}
          logoImageUrl={block.logoImage.url}
          logoImageAlt={block.logoImage.alt}
          personsTitle={getValueOrDefault(block.personsTitle, undefined)}
        />
      );
    }

    /**
     * Features
     */
    if (isFeatureWithDemo(block)) {
      return (
        <FeatureWithDemoImplementation
          key={`${block.acfFcLayout}-${i}`}
          {...block}
        />
      );
    }
    if (isFeatureTwoByTwoGrid(block)) {
      return (
        <FeatureTwoByTwoGrid key={`${block.acfFcLayout}-${i}`} {...block} />
      );
    }
    if (isFeatureTwoTextColumns(block)) {
      return (
        <FeatureTwoTextColumns
          key={`${block.acfFcLayout}-${i}`}
          {...block}
          imageUrl={block.image?.url}
          imageAlt={block.image?.alt}
          leftParagraphs={block.leftParagraphs.map(
            ({ paragraph }) => paragraph
          )}
          rightParagraphs={block.rightParagraphs.map(
            ({ paragraph }) => paragraph
          )}
        />
      );
    }
    if (isFeatureWithCTAs(block)) {
      return (
        <FeatureWithCTAs
          key={`${block.acfFcLayout}-${i}`}
          blockIndex={i}
          {...block}
        />
      );
    }

    /**
     * Newsletters
     */
    if (isNewsletterSignup(block)) {
      const { ad } = block;
      return (
        <NewsletterSignup
          appearance={block.appearance}
          blockIndex={i}
          description={ad.newsletterDescription}
          imageSrc={getValueOrDefault(block.backgroundImage?.url, null)}
          key={`${block.acfFcLayout}-${ad.slug}-${i}`}
          iterableListId={ad.iterableListId}
          newsletterSlug={ad.slug}
          title={ad.newsletterTitle}
          variant={block.variant}
        />
      );
    }
    /**
     * WYSIWYG
     */
    if (isWysiwygBlock(block)) {
      return (
        <WysiwygBlock
          key={`${block.acfFcLayout}-${i}`}
          appearance={block.appearance}
          wysiwygTextField={block.wysiwygTextField}
        />
      );
    }

    if (isYoutubeEmbedBlock(block)) {
      return (
        <YouTubeEmbedBlock
          src={block.videoUrl}
          title={`YouTube video player ${i + 1}`}
          key={`${block.acfFcLayout}-${i}`}
        />
      );
    }

    if (isTaggedPostsBlock(block)) {
      const contentItems = block.contentTags;
      return (
        <div key={`${block.acfFcLayout}-${i}`}>
          {block.header ? (
            <Headline level={2} as="h2" blockCase>
              {block.header}
            </Headline>
          ) : null}
          {block.description ? (
            <Body className="mt-xs text-secondary-text">
              {block.description}
            </Body>
          ) : null}
          <ContentItemGrid
            key={`${block.acfFcLayout}-${i}`}
            className="mx-auto mt-l w-full gap-y-l"
            itemBorder={false}
            variant={"3-item-row"}
          >
            {contentItems.map((contentItem, key) => (
              <ContentItemWithImage
                key={key}
                imagePosition="top"
                imageSrc={contentItem.image ? contentItem.image.source : ""}
                headline={contentItem.title}
                hasResponsiveHeadline={true}
                headlineHref={getRouteFromPostType(
                  contentItem.type as PostToRoutePostTypes,
                  contentItem.slug
                )}
              />
            ))}
          </ContentItemGrid>

          {block.ctaUrl ? (
            <Link
              className="mt-l flex align-middle font-semibold"
              href={block.ctaUrl.url}
              endAdornmentIcon={ArrowRightIcon}
            >
              {block.ctaUrl.title}
            </Link>
          ) : null}
        </div>
      );
    }

    /**
     * Error states
     *
     * Only shown in preview mode, otherwise do not render.
     */
    if (preview) {
      if (isUnknownBlockType(block)) {
        return (
          <UnknownBlockType key={`${block.acfFcLayout}-${i}`} block={block} />
        );
      }
      if (isInvalidPropertiesError(block)) {
        return (
          <InvalidProperties key={`${block.acfFcLayout}-${i}`} {...block} />
        );
      }
    }

    return null;
  });

  return <>{contentBlockComponents}</>;
};
